import { useQuery } from '@tanstack/react-query';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis, ComposedChart,
} from 'recharts';
import { Header } from '../components/layout/Header';
import { dashboardApi } from '../api/dashboard';
import { recommendationsApi, trendsApi, type ForecastRow, type ForecastModel } from '../api/modules';

const chartTheme = {
  axis: { fontSize: 11, fill: '#5A5A5A', fontFamily: 'IBM Plex Sans' },
  axisMono: { fontSize: 10, fill: '#5A5A5A', fontFamily: 'IBM Plex Mono' },
  tooltip: {
    backgroundColor: '#0A2540', border: 'none', borderRadius: 2, color: 'white',
    fontFamily: 'IBM Plex Sans', fontSize: 12,
  },
  legend: { fontFamily: 'IBM Plex Sans', fontSize: 11, color: '#0A2540', paddingTop: 12 },
};

export function AIAnalyticsPage() {
  const recsAll = useQuery({
    queryKey: ['recommendations', 'all'],
    queryFn: () => recommendationsApi.list({ status: 'any', page_size: 100 }),
  });
  const kpi = useQuery({ queryKey: ['dashboard', 'kpi-strip'], queryFn: dashboardApi.kpiStrip });
  const uptake = useQuery({ queryKey: ['trends', 'scheme-uptake'], queryFn: () => trendsApi.schemeUptake(12) });
  const forecast = useQuery<{ data: ForecastRow[]; model: ForecastModel }>({
    queryKey: ['trends', 'forecast'],
    queryFn: trendsApi.forecast,
  });
  const sla = useQuery({ queryKey: ['trends', 'sla-performance'], queryFn: () => trendsApi.slaPerformance(12) });
  const services = useQuery({ queryKey: ['trends', 'services-by-type'], queryFn: () => trendsApi.servicesByType(12) });
  const anomalies = useQuery({ queryKey: ['trends', 'anomalies'], queryFn: trendsApi.anomalies });

  const totalRecs = recsAll.data?.data.length ?? 0;
  const accepted = recsAll.data?.data.filter((r) => r.status === 'accepted').length ?? 0;
  const decided = (recsAll.data?.data.filter((r) => r.status === 'accepted' || r.status === 'rejected').length) ?? 0;
  const acceptanceRate = decided === 0 ? 0 : Math.round((accepted / decided) * 100);

  return (
    <>
      <Header breadcrumbs={['AI Engine', 'Analytics']} />
      <div className="px-8 pt-7 pb-2">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-navy-800/50 mb-1">CCC-AI Engine</div>
            <h1 className="serif-display text-3xl text-navy-800 font-medium">AI Analytics</h1>
            <p className="text-sm text-navy-800/60 mt-1">
              Trends · Forecasts · Pattern detection · Model performance
            </p>
          </div>
          {forecast.data && (
            <div className="text-right text-[11px] text-navy-800/50 font-numeric">
              Forecast model: <span className="font-medium text-navy-800/70">{forecast.data.model.name} {forecast.data.model.version}</span><br />
              Trained on last {forecast.data.model.training_window_months} months · {forecast.data.model.confidence_interval} CI
            </div>
          )}
        </div>
      </div>

      <div className="px-8 py-5 space-y-5 overflow-y-auto">
        {/* Metric cards */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Model accuracy',         value: '89.4%',                              sub: '+2.1pp vs last quarter' },
            { label: 'Recommendations (30d)',  value: totalRecs.toLocaleString('en-IN'),     sub: 'across 8 model types' },
            { label: 'Actioned by Director',   value: `${acceptanceRate}%`,                  sub: 'acceptance rate' },
            { label: 'Anomalies detected',     value: String(kpi.data?.sla_breaches.value ?? '…'), sub: 'last 30 days' },
          ].map((c) => (
            <div key={c.label} className="bg-white border border-cream-200 p-4 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-navy-800/50">{c.label}</div>
              <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1">{c.value}</div>
              <div className="text-[11px] text-navy-800/60 mt-1.5 font-numeric">{c.sub}</div>
            </div>
          ))}
        </div>

        {/* Trend + Forecast */}
        <section className="grid grid-cols-2 gap-5">
          <div className="bg-white border border-cream-200 rounded-sm">
            <div className="px-5 py-4 border-b border-cream-100">
              <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">Scheme uptake trend</h3>
              <p className="text-xs text-navy-800/60 mt-3">12-month rolling · Beneficiaries by scheme · Live from /api/dashboard/trends/scheme-uptake</p>
            </div>
            <div className="p-5 pt-3" style={{ height: 240 }}>
              {uptake.isLoading ? (
                <div className="h-full bg-cream-50 animate-pulse rounded-sm" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={uptake.data ?? []} margin={{ top: 10, right: 10, bottom: 20, left: -10 }}>
                    <CartesianGrid stroke="#F5F0E6" vertical={false} />
                    <XAxis dataKey="month" tick={chartTheme.axis} stroke="#D8CFBA" />
                    <YAxis tick={chartTheme.axisMono} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={chartTheme.tooltip} />
                    <Legend wrapperStyle={chartTheme.legend} iconType="circle" />
                    <Area type="monotone" dataKey="Goat"  stroke="#0A2540" fill="rgba(10,37,64,0.08)"  strokeWidth={2} />
                    <Area type="monotone" dataKey="Sheep" stroke="#E8740C" fill="rgba(232,116,12,0.08)" strokeWidth={2} />
                    <Area type="monotone" dataKey="Pig"   stroke="#1F4E7A" fill="rgba(31,78,122,0.08)"  strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-white border border-cream-200 rounded-sm">
            <div className="px-5 py-4 border-b border-cream-100 flex items-center justify-between">
              <div>
                <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">FY 2026-27 forecast</h3>
                <p className="text-xs text-navy-800/60 mt-3">
                  12 months actual + 6 month projection · 90% confidence interval
                </p>
              </div>
              <span className="text-[10px] bg-saffron-100 text-saffron-700 px-2 py-0.5 rounded font-medium">PREDICTIVE</span>
            </div>
            <div className="p-5 pt-3" style={{ height: 240 }}>
              {forecast.isLoading ? (
                <div className="h-full bg-cream-50 animate-pulse rounded-sm" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={forecast.data?.data ?? []} margin={{ top: 10, right: 10, bottom: 20, left: -10 }}>
                    <CartesianGrid stroke="#F5F0E6" vertical={false} />
                    <XAxis dataKey="month" tick={chartTheme.axis} stroke="#D8CFBA" />
                    <YAxis tick={chartTheme.axisMono} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={chartTheme.tooltip} />
                    <Legend wrapperStyle={chartTheme.legend} iconType="circle" />
                    <Area type="monotone" dataKey="upper" stroke="none" fill="rgba(232,116,12,0.10)" name="90% CI upper" legendType="none" />
                    <Area type="monotone" dataKey="lower" stroke="none" fill="white" name="90% CI lower" legendType="none" />
                    <Line type="monotone" dataKey="actual"    stroke="#0A2540" strokeWidth={2.5} dot={{ r: 3 }} name="Actual" />
                    <Line type="monotone" dataKey="predicted" stroke="#E8740C" strokeWidth={2.5} strokeDasharray="4 4" dot={{ r: 3 }} name="Predicted" />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </section>

        {/* SLA + Services */}
        <section className="grid grid-cols-2 gap-5">
          <div className="bg-white border border-cream-200 rounded-sm">
            <div className="px-5 py-4 border-b border-cream-100">
              <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">SLA performance</h3>
              <p className="text-xs text-navy-800/60 mt-3">% verified within 180-day SLA · target 80% · Live from /api/dashboard/trends/sla-performance</p>
            </div>
            <div className="p-5 pt-3" style={{ height: 220 }}>
              {sla.isLoading ? (
                <div className="h-full bg-cream-50 animate-pulse rounded-sm" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sla.data ?? []} margin={{ top: 10, right: 10, bottom: 20, left: -10 }}>
                    <CartesianGrid stroke="#F5F0E6" vertical={false} />
                    <XAxis dataKey="month" tick={chartTheme.axis} stroke="#D8CFBA" />
                    <YAxis tick={chartTheme.axisMono} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${v}%`} domain={[0, 100]} />
                    <Tooltip contentStyle={chartTheme.tooltip} />
                    <Legend wrapperStyle={chartTheme.legend} iconType="circle" />
                    <Line type="monotone" dataKey="on_time_pct" stroke="#16A34A" strokeWidth={2.5} dot={{ r: 3 }} name="On-time %" />
                    <Line type="monotone" dataKey="target"      stroke="#5A5A5A" strokeDasharray="3 3" dot={false} name="Target" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-white border border-cream-200 rounded-sm">
            <div className="px-5 py-4 border-b border-cream-100">
              <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">MAITRI services by type</h3>
              <p className="text-xs text-navy-800/60 mt-3">AI · Vaccination · Treatment · Live from /api/dashboard/trends/services-by-type</p>
            </div>
            <div className="p-5 pt-3" style={{ height: 220 }}>
              {services.isLoading ? (
                <div className="h-full bg-cream-50 animate-pulse rounded-sm" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={services.data ?? []} margin={{ top: 10, right: 10, bottom: 20, left: -10 }}>
                    <CartesianGrid stroke="#F5F0E6" vertical={false} />
                    <XAxis dataKey="month" tick={chartTheme.axis} stroke="#D8CFBA" />
                    <YAxis tick={chartTheme.axisMono} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={chartTheme.tooltip} />
                    <Legend wrapperStyle={chartTheme.legend} iconType="circle" />
                    <Bar dataKey="AI"           stackId="d" fill="#0A2540" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="Vaccination"  stackId="d" fill="#E8740C" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="Treatment"    stackId="d" fill="#1F4E7A" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </section>

        {/* Anomaly log */}
        <section className="bg-white border border-cream-200 rounded-sm">
          <div className="px-5 py-4 border-b border-cream-100">
            <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">Anomaly detection log</h3>
            <p className="text-xs text-navy-800/60 mt-3">Most recent 12 anomalies · derived from alerts pipeline</p>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream-50">
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">ID</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Detected</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Module</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Location</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Severity</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Title</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {anomalies.isLoading || !anomalies.data ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-t border-cream-100"><td colSpan={7} className="px-3 py-3"><div className="h-3 bg-cream-100 rounded animate-pulse" /></td></tr>
                ))
              ) : (
                anomalies.data.map((a) => (
                  <tr key={a.id} className="border-t border-cream-100 hover:bg-cream-50">
                    <td className="px-3 py-2.5 font-numeric text-navy-800/70 text-xs">{a.id}</td>
                    <td className="px-3 py-2.5 font-numeric text-navy-800/70 text-xs">
                      {new Date(a.triggered_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="px-3 py-2.5 text-navy-800/70 text-xs">{a.module}</td>
                    <td className="px-3 py-2.5 text-navy-800/70 text-xs">{a.location}</td>
                    <td className="px-3 py-2.5"><span className={`pill ${a.severity === 'critical' ? 'pill-red' : a.severity === 'warning' ? 'pill-amber' : 'pill-blue'}`}>{a.severity}</span></td>
                    <td className="px-3 py-2.5 font-medium text-navy-800">{a.title}</td>
                    <td className="px-3 py-2.5 text-xs"><span className="pill pill-navy">{a.status}</span></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      </div>
    </>
  );
}
